import React, { useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { UserContext } from "../../UserContext";
import "./styles.css";
import { FaTasks, FaBug, FaTractor, FaUsers } from "react-icons/fa";
import WeatherDashboardSummary from "../Weather/WeatherDashboardSummary";

// Reusable component for each summary card
const SummaryCard = ({ icon, title, value, detail, color, onClick }) => (
  <div className="summary-card" onClick={onClick} style={{ cursor: onClick ? 'pointer' : 'default' }}>
    <div className="card-icon" style={{ backgroundColor: color }}>
      {icon}
    </div>
    <div className="card-content">
      <h3 className="card-title">{title}</h3>
      <p className="card-value">{value}</p>
      {detail && <p className="card-detail">{detail}</p>}
    </div>
  </div>
);

// =========================================================================
// 1. STATIC MOCK DATA FOR TESTING
// =========================================================================
const staticSummaryData = {
  tasks: { completed: 7, total: 10 },
  problems: { count: 3 },
  fields: { count: 12 },
  employees: { count: 25 },
};

const Dashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userData, token } = useContext(UserContext);
  const [summaryData, setSummaryData] = useState({
    tasks: null,
    problems: null,
    fields: null,
    employees: null,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [fetchingTasks, setFetchingTasks] = useState(false);
  console.log("user data", userData);
  console.log("token", token);
  // =========================================================================
  // 2. USE EFFECT FOR STATIC DATA (For UI Testing)
  // This loads the mock data after a short delay to simulate a network request.
  // =========================================================================
  // useEffect(() => {
  //   setLoading(true);
  //   const timer = setTimeout(() => {
  //     setSummaryData(staticSummaryData);
  //     setLoading(false);
  //   }, 500); // 0.5 second delay

  //   // Cleanup timer on component unmount
  //   return () => clearTimeout(timer);
  // }, []); // Empty dependency array means this runs only once

  // =========================================================================
  // 3. LIVE API DATA (Commented Out)
  // When you are ready to connect to the live API, comment out the useEffect
  // above and uncomment this entire block.
  // =========================================================================

  useEffect(() => {
    if (!userData || userData.length === 0) {
      setLoading(false);
      setError("User data not available. Cannot load dashboard.");
      return;
    }

    const farmId = userData?.Farm?.id;
    const tenantId = userData?.Tenant?.id;

    if (!farmId || !tenantId) {
      setLoading(false);
      setError("Farm or Tenant ID is missing.");
      return;
    }

    const fetchDashboardData = async () => {
      setLoading(true);
      setError("");

      const startDate = new Date().setHours(0, 0, 0, 0);
      const endDate = new Date().setHours(23, 59, 59, 999);
      console.log("start date", startDate);
      console.log("end date", endDate);
      const authHeaders = { headers: { Authorization: token } };

      try {
        const requests = [
          axios.get(`/api/task/overview/${farmId}`, {
            params: { startDate, endDate },
            ...authHeaders,
          }),
          axios.get(`/api/problem/overview/${farmId}`, {
            params: { startDate, endDate },
            ...authHeaders,
          }),
          axios.get(`/api/field/overview/${farmId}`, authHeaders),
          axios.get(`/api/emp/overview/${tenantId}/${farmId}`, authHeaders),
        ];

        const [taskRes, problemRes, fieldRes, employeeRes] = await Promise.all(
          requests
        );

        // ---- UPDATED DATA PARSING LOGIC ----
        // The API response contains the data in snake_case format (e.g., completed_tasks)
        const taskData = taskRes.data.items[0];
        console.log("task data", taskData);
        console.log("probmles data", problemRes.data.items[0]);
        console.log("fields data", fieldRes.data.items[0]);
        console.log("employees data", employeeRes.data.items[0]);
        setSummaryData({
          tasks: {
            // Use 'completed_tasks' from the response
            completed: taskData?.completed_tasks || 0,
            // Use 'total_tasks' directly from the response
            total: taskData?.total_tasks || 0,
          },
          problems: {
            completed: problemRes.data.items[0]?.total_resolved_issues || 0,
            total: problemRes.data.items[0]?.total_issues || 0,
          },
          fields: {
            // Assuming the property is 'totalFields'
            count: fieldRes.data.items[0]?.totalFields || 0,
          },
          employees: {
            // Assuming the property is 'total_number_of_employees'
            count: employeeRes.data.items[0]?.total_number_of_employees || 0,
          },
        });
      } catch (err) {
        console.error("Failed to fetch dashboard data:", err);
        setError(t("Failed to load dashboard summary. Please try again."));
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [userData, token, t]);

  // Function to handle clicking on the Tasks card - navigate to Tasks page
  const handleTasksClick = () => {
    navigate('/tasks');
  };

  // Function to handle clicking on the Problems card - navigate to Problems page
  const handleProblemsClick = () => {
    navigate('/problems');
  };

  // Function to handle clicking on the Fields card - navigate to Fields page
  const handleFieldsClick = () => {
    navigate('/fields');
  };

  // Function to handle clicking on the Employees card - navigate to Employees page
  const handleEmployeesClick = () => {
    navigate('/employees');
  };

  // Function for testing API - fetches all task lists both ways (keep for debugging)
  const handleTasksAPITest = async () => {
    console.log("=== Tasks API Test Started ===");

    if (!userData || !token) {
      console.error("No user data or token available");
      return;
    }

    const farmId = userData?.Farm?.id;
    const employeeId = userData?.Employee?.id;
    const role = userData?.Role?.name;

    console.log("Testing tasks API with params:", {
      farmId,
      employeeId,
      role
    });

    setFetchingTasks(true);

    try {
      // Calculate date ranges for the new backend way
      const now = new Date();

      // TODAY: Start and end of today
      const todayStart = new Date(now);
      todayStart.setHours(0, 0, 0, 0);
      const todayEnd = new Date(now);
      todayEnd.setHours(23, 59, 59, 999);

      // FUTURE: Tomorrow onwards (no end date needed)
      const futureStart = new Date(now);
      futureStart.setDate(futureStart.getDate() + 1);
      futureStart.setHours(0, 0, 0, 0);

      // OLDER: Before today (no start date, end is yesterday)
      const olderEnd = new Date(now);
      olderEnd.setDate(olderEnd.getDate() - 1);
      olderEnd.setHours(23, 59, 59, 999);

      console.log("Date ranges for NEW way:", {
        today: {
          start: new Date(todayStart.getTime()).toISOString(),
          end: new Date(todayEnd.getTime()).toISOString(),
          startMs: todayStart.getTime(),
          endMs: todayEnd.getTime()
        },
        future: {
          start: new Date(futureStart.getTime()).toISOString(),
          startMs: futureStart.getTime()
        },
        older: {
          end: new Date(olderEnd.getTime()).toISOString(),
          endMs: olderEnd.getTime()
        }
      });

      // Make all 6 requests in parallel
      const [
        // Android way (without timestamps - just period)
        androidTodayResponse,
        androidFutureResponse,
        androidOlderResponse,
        // New way (with timestamps)
        newTodayResponse,
        newFutureResponse,
        newOlderResponse
      ] = await Promise.all([
        // === ANDROID WAY (current mobile app approach) ===
        // TODAY without timestamps
        axios.get(`/api/task/findByFarmId/${farmId}`, {
          params: {
            period: "today",
            employeeId: employeeId
          },
          headers: { Authorization: token }
        }),
        // FUTURE without timestamps
        axios.get(`/api/task/findByFarmId/${farmId}`, {
          params: {
            period: "future",
            employeeId: employeeId
          },
          headers: { Authorization: token }
        }),
        // OLDER without timestamps
        axios.get(`/api/task/findByFarmId/${farmId}`, {
          params: {
            period: "older",
            employeeId: employeeId
          },
          headers: { Authorization: token }
        }),

        // === NEW WAY (with timestamps) ===
        // TODAY with both timestamps
        axios.get(`/api/task/findByFarmId/${farmId}`, {
          params: {
            period: "today",
            fromDate: todayStart.getTime(),
            toDate: todayEnd.getTime(),
            employeeId: employeeId
          },
          headers: { Authorization: token }
        }),
        // FUTURE with start date only
        axios.get(`/api/task/findByFarmId/${farmId}`, {
          params: {
            period: "future",
            fromDate: futureStart.getTime(),
            employeeId: employeeId
          },
          headers: { Authorization: token }
        }),
        // OLDER with end date only
        axios.get(`/api/task/findByFarmId/${farmId}`, {
          params: {
            period: "older",
            toDate: olderEnd.getTime(),
            employeeId: employeeId
          },
          headers: { Authorization: token }
        })
      ]);

      // Log Android way results
      console.log("\n" + "=".repeat(60));
      console.log("=== ANDROID WAY (No Timestamps - Current Mobile App) ===");
      console.log("=".repeat(60));

      console.log("\n--- TODAY Tasks (Android way) ---");
      console.log("Status:", androidTodayResponse.status);
      console.log("Number of tasks:", androidTodayResponse.data.items?.length || 0);
      if (androidTodayResponse.data.items?.length > 0) {
        androidTodayResponse.data.items.forEach((task, index) => {
          console.log(`Task ${index + 1}:`, {
            id: task.id,
            name: task.name,
            status: task.status,
            dateFrom: task.dateFrom ? new Date(task.dateFrom).toLocaleString() : null,
            dateTo: task.dateTo ? new Date(task.dateTo).toLocaleString() : null
          });
        });
      }

      console.log("\n--- FUTURE Tasks (Android way) ---");
      console.log("Status:", androidFutureResponse.status);
      console.log("Number of tasks:", androidFutureResponse.data.items?.length || 0);
      if (androidFutureResponse.data.items?.length > 0) {
        androidFutureResponse.data.items.forEach((task, index) => {
          console.log(`Task ${index + 1}:`, {
            id: task.id,
            name: task.name,
            status: task.status,
            dateFrom: task.dateFrom ? new Date(task.dateFrom).toLocaleString() : null,
            dateTo: task.dateTo ? new Date(task.dateTo).toLocaleString() : null
          });
        });
      }

      console.log("\n--- OLDER Tasks (Android way) ---");
      console.log("Status:", androidOlderResponse.status);
      console.log("Number of tasks:", androidOlderResponse.data.items?.length || 0);
      if (androidOlderResponse.data.items?.length > 0) {
        androidOlderResponse.data.items.forEach((task, index) => {
          console.log(`Task ${index + 1}:`, {
            id: task.id,
            name: task.name,
            status: task.status,
            dateFrom: task.dateFrom ? new Date(task.dateFrom).toLocaleString() : null,
            dateTo: task.dateTo ? new Date(task.dateTo).toLocaleString() : null
          });
        });
      }

      // Log New way results
      console.log("\n" + "=".repeat(60));
      console.log("=== NEW WAY (With Timestamps - Updated Backend) ===");
      console.log("=".repeat(60));

      console.log("\n--- TODAY Tasks (New way with timestamps) ---");
      console.log("Request params:", { fromDate: todayStart.getTime(), toDate: todayEnd.getTime() });
      console.log("Status:", newTodayResponse.status);
      console.log("Number of tasks:", newTodayResponse.data.items?.length || 0);
      if (newTodayResponse.data.items?.length > 0) {
        newTodayResponse.data.items.forEach((task, index) => {
          console.log(`Task ${index + 1}:`, {
            id: task.id,
            name: task.name,
            status: task.status,
            dateFrom: task.dateFrom ? new Date(task.dateFrom).toLocaleString() : null,
            dateTo: task.dateTo ? new Date(task.dateTo).toLocaleString() : null
          });
        });
      }

      console.log("\n--- FUTURE Tasks (New way with start timestamp) ---");
      console.log("Request params:", { fromDate: futureStart.getTime() });
      console.log("Status:", newFutureResponse.status);
      console.log("Number of tasks:", newFutureResponse.data.items?.length || 0);
      if (newFutureResponse.data.items?.length > 0) {
        newFutureResponse.data.items.forEach((task, index) => {
          console.log(`Task ${index + 1}:`, {
            id: task.id,
            name: task.name,
            status: task.status,
            dateFrom: task.dateFrom ? new Date(task.dateFrom).toLocaleString() : null,
            dateTo: task.dateTo ? new Date(task.dateTo).toLocaleString() : null
          });
        });
      }

      console.log("\n--- OLDER Tasks (New way with end timestamp) ---");
      console.log("Request params:", { toDate: olderEnd.getTime() });
      console.log("Status:", newOlderResponse.status);
      console.log("Number of tasks:", newOlderResponse.data.items?.length || 0);
      if (newOlderResponse.data.items?.length > 0) {
        newOlderResponse.data.items.forEach((task, index) => {
          console.log(`Task ${index + 1}:`, {
            id: task.id,
            name: task.name,
            status: task.status,
            dateFrom: task.dateFrom ? new Date(task.dateFrom).toLocaleString() : null,
            dateTo: task.dateTo ? new Date(task.dateTo).toLocaleString() : null
          });
        });
      }

      // Comparison summary
      console.log("\n" + "=".repeat(60));
      console.log("=== COMPARISON SUMMARY ===");
      console.log("=".repeat(60));

      const androidTodayCount = androidTodayResponse.data.items?.length || 0;
      const androidFutureCount = androidFutureResponse.data.items?.length || 0;
      const androidOlderCount = androidOlderResponse.data.items?.length || 0;
      const androidTotal = androidTodayCount + androidFutureCount + androidOlderCount;

      const newTodayCount = newTodayResponse.data.items?.length || 0;
      const newFutureCount = newFutureResponse.data.items?.length || 0;
      const newOlderCount = newOlderResponse.data.items?.length || 0;
      const newTotal = newTodayCount + newFutureCount + newOlderCount;

      console.log("\nAndroid Way (No timestamps):");
      console.log("  TODAY:", androidTodayCount, "tasks");
      console.log("  FUTURE:", androidFutureCount, "tasks");
      console.log("  OLDER:", androidOlderCount, "tasks");
      console.log("  TOTAL:", androidTotal, "tasks");

      console.log("\nNew Way (With timestamps):");
      console.log("  TODAY:", newTodayCount, "tasks");
      console.log("  FUTURE:", newFutureCount, "tasks");
      console.log("  OLDER:", newOlderCount, "tasks");
      console.log("  TOTAL:", newTotal, "tasks");

      console.log("\nTask Overview (from dashboard):");
      console.log("  TODAY:", summaryData.tasks?.total || 0, "tasks");
      console.log("  COMPLETED:", summaryData.tasks?.completed || 0, "tasks");

      // Check for discrepancies
      console.log("\n" + "=".repeat(60));
      console.log("=== DISCREPANCY ANALYSIS ===");
      console.log("=".repeat(60));

      if (androidTodayCount !== newTodayCount) {
        console.warn("⚠️ TODAY DISCREPANCY:");
        console.warn(`  Android way: ${androidTodayCount} tasks`);
        console.warn(`  New way: ${newTodayCount} tasks`);
        console.warn(`  Difference: ${Math.abs(androidTodayCount - newTodayCount)} tasks`);
      } else {
        console.log("✅ TODAY counts match between Android and New way");
      }

      if (androidTodayCount !== (summaryData.tasks?.total || 0)) {
        console.warn("⚠️ OVERVIEW DISCREPANCY (Android way):");
        console.warn(`  Overview shows: ${summaryData.tasks?.total} tasks for TODAY`);
        console.warn(`  Android way returns: ${androidTodayCount} tasks`);
      }

      if (newTodayCount !== (summaryData.tasks?.total || 0)) {
        console.warn("⚠️ OVERVIEW DISCREPANCY (New way):");
        console.warn(`  Overview shows: ${summaryData.tasks?.total} tasks for TODAY`);
        console.warn(`  New way returns: ${newTodayCount} tasks`);
      }

      if (androidTodayCount === newTodayCount && androidTodayCount === (summaryData.tasks?.total || 0)) {
        console.log("✅ All TODAY counts match perfectly!");
      }

    } catch (error) {
      console.error("=== Error fetching task lists ===");
      console.error("Error details:", error.response?.data || error.message);
      console.error("Full error:", error);
    } finally {
      setFetchingTasks(false);
    }
  };

  if (loading) {
    return <div className="dashboard-message">{t("Loading Dashboard...")}</div>;
  }

  if (error) {
    return <div className="dashboard-message error">{error}</div>;
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>{t("Dashboard Summary")}</h1>
        {/* <button className="details-button">{t("Details")}</button> */}
      </div>
      {/* weather summary feature */}
      <WeatherDashboardSummary />
      <div className="summary-grid" style={{ marginTop: "25px" }}></div>
      <div className="summary-grid">
        <SummaryCard
          icon={<FaTasks />}
          title={t("Tasks for Today")}
          value={`${summaryData.tasks?.completed || 0} / ${
            summaryData.tasks?.total || 0
          }`}
          detail={
            summaryData.tasks?.total > 0
              ? `${Math.round(
                  (summaryData.tasks.completed / summaryData.tasks.total) * 100
                )}% ${t("Completed")}`
              : `0% ${t("Completed")}`
          }
          color="rgba(44, 42, 108, 0.2)"
          onClick={handleTasksClick}
        />
        <SummaryCard
          icon={<FaBug />}
          title={t("Problems of the Day")}
          // value={summaryData.problems?.count || 0}
          value={`${summaryData.problems?.completed || 0} / ${
            summaryData.problems?.total || 0
          }`}
          detail={
            summaryData.problems?.total > 0
              ? `${Math.round(
                  (summaryData.problems.completed /
                    summaryData.problems.total) *
                    100
                )}% ${t("Solved")}`
              : `0% ${t("Solved")}`
          }
          color="rgba(217, 83, 79, 0.2)"
          onClick={handleProblemsClick}
        />
        <SummaryCard
          icon={<FaTractor />}
          title={t("Total Fields")}
          value={summaryData.fields?.count || 0}
          detail={t("Active Sectors")}
          color="rgba(92, 184, 92, 0.2)"
          onClick={handleFieldsClick}
        />
        <SummaryCard
          icon={<FaUsers />}
          title={t("Total Employees")}
          value={summaryData.employees?.count || 0}
          detail={t("Active Staff")}
          color="rgba(240, 173, 78, 0.2)"
          onClick={handleEmployeesClick}
        />
      </div>
    </div>
  );
};

export default Dashboard;
